import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Dumbbell, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../../api";
import { MEMBER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

// UX-MEM-01 §1 SCR-P03-00: phone + OTP, no password — a brand-new backend auth surface this
// phase (`routers/member_auth.py`; flagged + approved separately, see PRD). Branding is
// necessarily generic until a phone is submitted and the member's org is resolved (there's no
// way to know which gym before that) — the OTP-request response hands back
// `organization_name`/`organization_branding`, applied from the code-entry step onward.
//
// UX-POLISH-02: full-bleed vertical (9:16, mobile-native) video hero — members overwhelmingly
// sign in on a phone, per product decision — with a flat 40% black overlay for legibility. On
// md+ (web) viewports the whole panel gets an outer padding + rounded corners so it reads as a
// framed video card rather than a phone-only full-bleed background stretched across a desktop
// window. Same blob-fetch workaround as Login.jsx's hero (see that file's comment): this dev
// proxy's blanket `Cache-Control: no-store` breaks a plain <video src> element's native
// byte-range buffering, so the file is fetched once and handed to <video> as a blob: URL.
export default function MemberLogin() {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [orgName, setOrgName] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);

  useEffect(() => {
    let blobUrl;
    let cancelled = false;
    // H.264/mp4 is the universally-safe default for real devices (mobile Safari in particular
    // has zero VP9/WebM support) — webm is only ever a fallback for the rare browser that can't
    // decode H.264 at all.
    const probe = document.createElement("video");
    const path = probe.canPlayType('video/mp4; codecs="avc1.42E01E"')
      ? "/videos/member-login-bg.mp4" : "/videos/member-login-bg.webm";
    fetch(path)
      .then((res) => res.blob())
      .then((blob) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setVideoSrc(blobUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, []);

  const requestOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.post("/auth/member/otp/request", { phone: phone.trim() });
      setOrgName(res.data.organization_name);
      setStep("otp");
    } catch (e) {
      setError(formatApiError(e).message);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/member/otp/verify", { phone: phone.trim(), code: code.trim() });
      navigate("/member/home");
    } catch (e) {
      setError(formatApiError(e).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="member-app min-h-screen w-full md:p-8 lg:p-12" style={{ background: "#0A0A0A" }}>
      <div data-testid="member-login-page" className="relative w-full h-[100vh] md:h-[calc(100vh-4rem)] lg:h-[calc(100vh-6rem)] md:rounded-[24px] overflow-hidden flex items-center justify-center px-6">
        {videoSrc && (
          <video
            autoPlay muted loop playsInline preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
            aria-hidden="true"
            src={videoSrc}
          />
        )}
        {/* Flat 40% black overlay, per spec, so login details stay legible over any video frame */}
        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.4)" }} />

        <motion.div {...fadeUp(reducedMotion)} className="relative z-10 w-full max-w-sm">
          <div data-testid={MEMBER.loginBrand} className="flex flex-col items-center mb-10 text-center">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: "#D6202B" }}>
              <Dumbbell size={26} color="#fff" strokeWidth={1.5} />
            </div>
            <h1 className="font-heading text-4xl font-black text-white tracking-tight">{orgName || "FitFrikArena"}</h1>
            <p className="text-sm mt-1" style={{ color: "#A1A1AA" }}>{step === "phone" ? "Sign in with your phone number" : `Enter the code sent to ${phone}`}</p>
          </div>

          {step === "phone" ? (
            <form onSubmit={requestOtp} className="space-y-3">
              <Input
                data-testid={MEMBER.phoneInput} type="tel" placeholder="Phone number" value={phone}
                onChange={(e) => setPhone(e.target.value)} className="h-12 text-white bg-[#141414]/80 border-white/10 placeholder:text-zinc-500"
              />
              {error && <p data-testid={MEMBER.loginError} className="text-sm" style={{ color: "#D6202B" }}>{error}</p>}
              <Button data-testid={MEMBER.phoneSubmitButton} disabled={!phone.trim() || busy} type="submit" className="w-full h-12 text-white font-semibold active:scale-95 transition-transform" style={{ background: "#D6202B" }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Send code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-3">
              <Input
                data-testid={MEMBER.otpInput} inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code}
                onChange={(e) => setCode(e.target.value)} className="h-12 text-white bg-[#141414]/80 border-white/10 placeholder:text-zinc-500 text-center text-lg tracking-[0.3em]"
              />
              {error && <p data-testid={MEMBER.loginError} className="text-sm" style={{ color: "#D6202B" }}>{error}</p>}
              <Button data-testid={MEMBER.otpSubmitButton} disabled={!code.trim() || busy} type="submit" className="w-full h-12 text-white font-semibold gap-2 active:scale-95 transition-transform" style={{ background: "#D6202B" }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <><ShieldCheck size={16} /> Verify & continue</>}
              </Button>
              <button data-testid={MEMBER.otpResendButton} type="button" onClick={requestOtp} className="w-full text-sm py-2" style={{ color: "#A1A1AA" }}>
                Resend code
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
